/**
 * Telling us something.
 *
 * The whole of step 35's second half on the client, and it is deliberately three states and one
 * field. A beta form that asked for a category, a severity and a screenshot is a form nobody
 * fills in; what is wanted is the sentence somebody was already thinking.
 *
 * **The route travels, and the URL does not.** Where they were is what makes a sentence
 * actionable — "this is confusing" about the editor and about the library are different bugs —
 * and a query string is content. The Worker strips one anyway; sending only the path means this
 * side does not rely on it having to.
 *
 * **Sent is a state, not a toast.** A message that vanishes after three seconds is a message
 * somebody who looked away never saw, and the one thing this dialog owes them is certainty that
 * their words went somewhere.
 */
import { useState } from 'react';

import { Button } from '../ui/controls';
import { Modal } from '../ui/Modal';

import { sendFeedback } from './api';

export interface FeedbackDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
}

type Phase =
  | { readonly status: 'writing' }
  | { readonly status: 'sending' }
  | { readonly status: 'sent' }
  | { readonly status: 'failed'; readonly message: string };

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps): React.JSX.Element {
  const [message, setMessage] = useState('');
  const [phase, setPhase] = useState<Phase>({ status: 'writing' });

  const close = (next: boolean): void => {
    onOpenChange(next);
    if (!next) {
      // Reset on the way out rather than on the way in: a dialog reopened by mistake should not
      // still be showing "Thank you" about something sent an hour ago.
      setMessage('');
      setPhase({ status: 'writing' });
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={close}
      size="narrow"
      title="Send feedback"
      description="What is working, what is not, what is missing."
      autoFocusSelector="[data-feedback-message]"
    >
      {phase.status === 'sent' ? (
        <div className="grid gap-3 p-5">
          <p className="font-medium">Thank you — that reached us.</p>
          <p className="text-ink-muted text-sm">
            It is read by the people building this, and it is the main reason the beta is small.
          </p>
          <div className="flex justify-end">
            <Button
              tone="primary"
              onClick={() => {
                close(false);
              }}
            >
              Close
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="grid gap-3 p-5"
          onSubmit={(event) => {
            event.preventDefault();
            const written = message.trim();
            if (written === '' || phase.status === 'sending') return;

            setPhase({ status: 'sending' });
            // The route, not the URL — see the header.
            void sendFeedback(written, window.location.pathname).then((result) => {
              setPhase(
                result.ok
                  ? { status: 'sent' }
                  : { status: 'failed', message: result.error.message },
              );
            });
          }}
        >
          <label className="text-sm font-medium" htmlFor="feedback-message">
            What would you like to tell us?
          </label>
          <textarea
            id="feedback-message"
            data-feedback-message
            value={message}
            onChange={(event) => {
              setMessage(event.target.value);
            }}
            rows={6}
            maxLength={2000}
            disabled={phase.status === 'sending'}
            className="border-edge bg-surface text-ink focus:border-edge-strong resize-y rounded-lg border p-3 text-sm outline-none"
          />

          {phase.status === 'failed' && (
            <p className="border-danger/50 rounded-panel bg-surface border p-3 text-sm">
              {phase.message}
            </p>
          )}

          <p className="text-ink-faint text-xs">
            We keep what you write and which screen you were on. Nothing else about this moment.
          </p>

          <div className="flex justify-end">
            <Button
              tone="primary"
              size="lg"
              type="submit"
              disabled={phase.status === 'sending' || message.trim() === ''}
            >
              {phase.status === 'sending' ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </form>
      )}
    </Modal>
  );
}
